import React, { useState, useEffect, FC, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormSteps } from '../../hooks/useFormSteps';
import Stepper from '../../components/ui/Stepper';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Loader from '../../components/ui/Loader';
import Select from '../../components/ui/Select';
import agentService from '../../services/agent.service';
import { AgentProfileFormData, ProfileFormErrors, StepDefinition } from '../../types/components';
import { LocalStorageUser } from '../../types/localStorage';
import { markProfileAsCompleted } from '../../utils/profileUtils';
import { Gender } from '../../types/api';

const AgentOnboarding: FC = (): React.JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const steps: StepDefinition[] = [
    { title: 'Profile', description: 'Personal and contact details' },
    { title: 'Experience', description: 'Work background' },
    { title: 'Sourcing', description: 'Industries and roles' },
    { title: 'Compliance', description: 'Documents and verification' },
    { title: 'Review & Submit', description: 'Confirm details' },
  ];

  const initialFormData: AgentProfileFormData = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: 'Male',
    address: '',
    agencyName: '',
    experience: '',
    specializations: [],
    services: [],
    feeStructure: '',
    credentials: '',
    portfolio: '',
    references: '',
    availability: [],
    profilePhoto: null,
    professionalTitle: '',
    professionalSummary: '',
    totalPlacements: 0,
    successRate: 0,
    avgSalaryPlaced: '',
    clientRetentionRate: 0,
    testimonials: '',
    notablePlacements: '',
    ref1Name: '',
    ref1Company: '',
    ref1Email: '',
    ref1Phone: '',
    ref2Name: '',
    ref2Company: '',
    ref2Email: '',
    ref2Phone: '',
    profileVisibility: false,
    emailNotifications: false,
    govtIdFront: undefined,
    govtIdBack: undefined,
    idProof: undefined,
    addressProof: undefined,
    industriesServed: '',
    geographicCoverage: '',
    avgTimeToFill: '',
    startTime: '',
    endTime: '',
    experienceDetails: [],
    certificationsDetails: [],
  };

  const {
    currentStep,
    formData,
    nextStep,
    prevStep,
    updateFormData,
    isFirstStep,
    isLastStep,
  } = useFormSteps<AgentProfileFormData>(steps, initialFormData);

  const validateStep = (): boolean => {
    const newErrors: ProfileFormErrors = {};
    
    switch (currentStep) {
      case 0: // Profile
        if (!formData.firstName?.trim()) newErrors.firstName = 'First name is required';
        if (!formData.lastName?.trim()) newErrors.lastName = 'Last name is required';
        if (!formData.email?.trim()) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
        if (!formData.phone?.trim()) newErrors.phone = 'Phone number is required';
        else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) newErrors.phone = 'Please enter a valid phone number';
        break;
      case 1: // Experience
        if (!formData.experience?.trim()) newErrors.experience = 'Years of experience is required';
        if (!formData.agencyName?.trim()) newErrors.agencyName = 'Agency name is required';
        break;
      case 2: // Sourcing
        if (formData.specializations?.length === 0) newErrors.specializations = 'Specializations are required';
        if (formData.services?.length === 0) newErrors.services = 'Services are required';
        break;
      case 3: // Compliance
        if (!formData.govtIdFront) newErrors.govtIdFront = 'Government ID (Front) is required';
        if (!formData.govtIdBack) newErrors.govtIdBack = 'Government ID (Back) is required';
        break;
      case 4: // Review & Submit
        return true; // No specific validation for review step
      default:
        break;
    }
    
    // Assuming updateFormData can also update errors
    // For now, we'll just set errors directly
    // setErrors(newErrors); // This would be handled by useFormSteps if it supported errors
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (): void => {
    if (validateStep()) {
      if (currentStep < steps.length - 1) {
        nextStep();
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrev = (): void => {
    if (currentStep > 0) {
      prevStep();
    }
  };

  const handleSubmit = async (): Promise<void> => {
    setLoading(true);
    try {
      // Tentative payload
      const payload: AgentProfileFormData = {
        ...formData,
        // Ensure file types are handled correctly for API submission if needed
      };
      // Fallback: use a generic endpoint if specific onboarding API not present
      // if (agentService.createOrUpdateProfile) {
      //   await agentService.createOrUpdateProfile(payload);
      // }
      // TODO requires User type 
      // markProfileAsCompleted('Agent'); // Mark profile as completed in local storage
      navigate('/agent/dashboard');
    } catch (e: any) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (fieldName: keyof AgentProfileFormData, e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      updateFormData({ [fieldName]: e.target.files[0] } as Partial<AgentProfileFormData>);
    }
  };

  const renderStepContent = (): React.JSX.Element | null => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Profile</h3>
            <Input
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ firstName: e.target.value })}
              required
              placeholder="Your first name"
            />
            <Input
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ lastName: e.target.value })}
              required
              placeholder="Your last name"
            />
            <Input
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ email: e.target.value })}
              required
              placeholder="you@example.com"
            />
            <Input
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ phone: e.target.value })}
              required
              placeholder="+91 90000 00000"
            />
            <Input
              label="Date of Birth"
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ dateOfBirth: e.target.value })}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <Select
                options={[
                  { value: '', label: 'Select Gender' },
                  { value: 'Male', label: 'Male' },
                  { value: 'Female', label: 'Female' },
                  { value: 'other', label: 'Other' },
                ]}
                value={formData.gender}
                onChange={(val) => updateFormData({ gender: val as Gender })}
              />
            </div>
            <Input
              label="Address"
              name="address"
              value={formData.address}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ address: e.target.value })}
              required
              placeholder="Your full address"
            />
            <Input
              label="Professional Title"
              name="professionalTitle"
              value={formData.professionalTitle || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ professionalTitle: e.target.value })}
              placeholder="e.g. Senior Recruiter"
            />
            <Input
              label="Professional Summary"
              name="professionalSummary"
              value={formData.professionalSummary || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ professionalSummary: e.target.value })}
              placeholder="Brief summary of your experience"
            />
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                {formData.profilePhoto ? (
                  <img src={URL.createObjectURL(formData.profilePhoto as Blob)} alt="Profile" className="w-24 h-24 rounded-lg object-cover" />
                ) : (
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}
              </div>
              <div>
                <label htmlFor="profile-photo" className="cursor-pointer">
                  <Button variant="outline" size="sm">Upload Photo</Button>
                </label>
                <input
                  id="profile-photo"
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={(e) => handleFileChange('profilePhoto', e)}
                />
                <p className="text-xs text-gray-500 mt-1">PNG, JPG, SVG up to 2MB</p>
              </div>
            </div>
          </div>
        );
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Experience</h3>
            <Input
              label="Agency Name"
              name="agencyName"
              value={formData.agencyName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ agencyName: e.target.value })}
              required
              placeholder="Your agency name"
            />
            <Input
              label="Years of Experience"
              type="number"
              name="experience"
              value={formData.experience}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ experience: e.target.value })}
              required
              placeholder="e.g. 5"
            />
            <Input
              label="Credentials"
              name="credentials"
              value={formData.credentials || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ credentials: e.target.value })}
              placeholder="e.g. Certified Recruitment Professional"
            />
            <Input
              label="Portfolio URL"
              type="url"
              name="portfolio"
              value={formData.portfolio || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ portfolio: e.target.value })}
              placeholder="https://yourportfolio.com"
            />
            <Input
              label="References"
              name="references"
              value={formData.references || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ references: e.target.value })}
              placeholder="Provide professional references"
            />
            <Input
              label="Availability"
              name="availability"
              value={formData.availability || ''}
              // onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ availability: e.target.value })} // TODO check type
              placeholder="e.g. Full-time, Part-time"
            />
            <Input
              label="Fee Structure"
              name="feeStructure"
              value={formData.feeStructure || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ feeStructure: e.target.value })}
              placeholder="e.g. Percentage of salary, Flat fee"
            />
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Sourcing Preferences</h3>
            <Input
              label="Specializations (comma-separated)"
              name="specializations"
              value={formData?.specializations?.join(', ')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ specializations: e.target.value.split(',').map(s => s.trim()) })}
              required
              placeholder="e.g. IT, Healthcare, Finance"
            />
            <Input
              label="Services Offered (comma-separated)"
              name="services"
              value={formData?.services?.join(', ')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ services: e.target.value.split(',').map(s => s.trim()) })}
              required
              placeholder="e.g. Executive Search, Staffing, RPO"
            />
            <Input
              label="Industries Served"
              name="industriesServed"
              value={formData.industriesServed || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ industriesServed: e.target.value })}
              placeholder="e.g. Technology, Finance, Healthcare"
            />
            <Input
              label="Geographic Coverage"
              name="geographicCoverage"
              value={formData.geographicCoverage || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ geographicCoverage: e.target.value })}
              placeholder="e.g. PAN India, APAC, Remote"
            />
            <Input
              label="Average Time to Fill (days)"
              type="number"
              name="avgTimeToFill"
              value={formData.avgTimeToFill || ''}
              onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ avgTimeToFill: e.target.value })}
              placeholder="e.g. 30"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Working Hours Start"
                type="time"
                name="startTime"
                value={formData.startTime || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ startTime: e.target.value })}
              />
              <Input
                label="Working Hours End"
                type="time"
                name="endTime"
                value={formData.endTime || ''}
                onChange={(e: ChangeEvent<HTMLInputElement>) => updateFormData({ endTime: e.target.value })}
              />
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Compliance</h3>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="mt-1 text-sm text-gray-900 font-medium">Government ID (Front)</div>
                <input
                  id="govt-id-front"
                  name="govtIdFront"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange('govtIdFront', e)}
                />
              </div>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <div className="mt-1 text-sm text-gray-900 font-medium">Government ID (Back)</div>
                <input
                  id="govt-id-back"
                  name="govtIdBack"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => handleFileChange('govtIdBack', e)}
                />
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Review</h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Profile</h4>
              <p><strong>First Name:</strong> {formData.firstName}</p>
              <p><strong>Last Name:</strong> {formData.lastName}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              <p><strong>Phone:</strong> {formData.phone}</p>
              <p><strong>Address:</strong> {formData.address}</p>
              <p><strong>Professional Title:</strong> {formData.professionalTitle || '—'}</p>
              <p><strong>Professional Summary:</strong> {formData.professionalSummary || '—'}</p>

              <h4 className="font-medium mb-2 mt-4">Experience</h4>
              <p><strong>Agency Name:</strong> {formData.agencyName}</p>
              <p><strong>Years of Experience:</strong> {formData.experience}</p>
              <p><strong>Credentials:</strong> {formData.credentials || '—'}</p>
              <p><strong>Portfolio:</strong> {formData.portfolio || '—'}</p>
              <p><strong>References:</strong> {formData.references || '—'}</p>
              <p><strong>Availability:</strong> {formData.availability || '—'}</p>
              <p><strong>Fee Structure:</strong> {formData.feeStructure || '—'}</p>

              <h4 className="font-medium mb-2 mt-4">Sourcing</h4>
              <p><strong>Specializations:</strong> {formData?.specializations?.join(', ') || '—'}</p>
              <p><strong>Services Offered:</strong> {formData?.services?.join(', ') || '—'}</p>
              <p><strong>Industries Served:</strong> {formData.industriesServed || '—'}</p>
              <p><strong>Geographic Coverage:</strong> {formData.geographicCoverage || '—'}</p>
              <p><strong>Avg. Time to Fill:</strong> {formData.avgTimeToFill || '—'}</p>
              <p><strong>Working Hours:</strong> {formData.startTime || '—'} - {formData.endTime || '—'}</p>

              <h4 className="font-medium mb-2 mt-4">Compliance</h4>
              <p><strong>ID Front:</strong> {formData.govtIdFront?.name || 'Not uploaded'}</p>
              <p><strong>ID Back:</strong> {formData.govtIdBack?.name || 'Not uploaded'}</p>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Agent Onboarding</h1>
        <p className="text-gray-600">Complete these steps to set up your agent profile</p>
      </div>

      <div className="mb-8">
        <Stepper steps={steps} currentStep={currentStep} />
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        {renderStepContent()}

        <div className="flex justify-between mt-8">
          <Button variant="outline" onClick={handlePrev} disabled={isFirstStep}>
            Previous
          </Button>
          {isLastStep ? (
            <Button onClick={handleSubmit} loading={loading} disabled={loading}>
              {loading ? <Loader size="sm" /> : 'Submit'}
            </Button>
          ) : (
            <Button onClick={handleNext}>Next</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AgentOnboarding;
